// DHA product page price estimator (WI-80).
//
// The pricing rules are plain functions (no DOM) so Node tests can exercise them
// (tools/price-estimator-review/pricing.test.mjs). The page part only wires the
// snippet's markup to those functions. The estimator never changes the quote
// form and publishes no events: it is a read-only convenience above the Request a Quote button.
//
// Rules: an estimate is for a set of four; the larger of the front and rear diameters sets the
// base price; brushing adds a flat per-set charge; a promotion is a message only and is never
// subtracted from the estimate.

(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.DHAPriceEstimator = api;
    api.start();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  var SIZES = [18, 19, 20, 21, 22];

  // "18:2599,19:2799" -> { 18: 2599, 19: 2799 }; anything that is not a positive whole number is dropped.
  function parseMap(text) {
    var map = {};
    String(text || '').split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var size = parseInt(parts[0], 10);
      var value = parseInt(parts[1], 10);
      if (size && value > 0) map[size] = value;
    });
    return map;
  }

  function money(amount) {
    return '$' + String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // cfg: { prices: {size: dollars}, brushing: dollars (0 = none), promoOn: bool, promos: {size: dollars} }
  // front / rear: a size number or null.
  function estimate(cfg, front, rear, brushed) {
    var hasFront = front != null;
    var hasRear = rear != null;
    if (!hasFront || !hasRear) {
      var prompt = !hasFront && !hasRear
        ? 'Select front and rear sizes to see an estimate.'
        : !hasFront ? 'Select a front size to see an estimate.' : 'Select a rear size to see an estimate.';
      return { ready: false, priceText: '$—', basisText: prompt, promo: null };
    }
    var big = Math.max(front, rear);
    var base = cfg.prices[big];
    if (!base) {
      return { ready: false, priceText: '$—', basisText: 'Request a quote and we’ll check pricing for this size.', promo: null };
    }
    var brushing = brushed && cfg.brushing > 0 ? cfg.brushing : 0;
    var basis = [
      front !== rear ? 'Priced at ' + big + '″, the larger size' : big + '″ front and rear',
      brushing ? 'includes brushing' : null
    ].filter(Boolean).join(' · ');
    var amount = cfg.promoOn ? cfg.promos[big] : 0;
    return {
      ready: true,
      total: base + brushing,
      priceText: money(base + brushing),
      basisText: basis,
      // A message only: the amount is never subtracted from total.
      promo: amount > 0 ? { size: big, amount: amount, text: money(amount) + ' off ' + big + '″ sets right now' } : null
    };
  }

  function configFrom(el) {
    return {
      prices: parseMap(el.getAttribute('data-prices')),
      promos: parseMap(el.getAttribute('data-promos')),
      brushing: parseInt(el.getAttribute('data-brushing'), 10) || 0,
      promoOn: el.getAttribute('data-promo-on') === 'true'
    };
  }

  function initEstimator(el) {
    var cfg = configFrom(el);
    var state = { front: null, rear: null, brushed: false };
    var price = el.querySelector('[data-dha-est-price]');
    var basis = el.querySelector('[data-dha-est-basis]');
    var promo = el.querySelector('[data-dha-est-promo]');
    var promoText = el.querySelector('[data-dha-est-promo-text]');
    var brush = el.querySelector('[data-dha-est-brush]');

    function render() {
      var result = estimate(cfg, state.front, state.rear, state.brushed);
      price.textContent = result.priceText;
      price.classList.toggle('dha-est__price--empty', !result.ready);
      basis.textContent = result.basisText;
      if (promo) {
        promo.hidden = !result.promo;
        if (result.promo && promoText) promoText.textContent = result.promo.text;
      }
      ['front', 'rear'].forEach(function (key) {
        el.querySelectorAll('[data-dha-est-size="' + key + '"] button').forEach(function (button) {
          button.setAttribute('aria-pressed', String(parseInt(button.getAttribute('data-size'), 10) === state[key]));
        });
      });
    }

    el.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-dha-est-size] button');
      if (!button || !el.contains(button)) return;
      var key = button.closest('[data-dha-est-size]').getAttribute('data-dha-est-size');
      var size = parseInt(button.getAttribute('data-size'), 10);
      state[key] = state[key] === size ? null : size;
      render();
    });
    if (brush) {
      brush.addEventListener('change', function () {
        state.brushed = brush.checked;
        render();
      });
    }
    render();
  }

  // Product descriptions are kept as written. On estimator pages a long one is clamped to about three
  // lines (CSS, only once this script adds the class) with a Read more control, so the estimator stays near the top.
  function initDescription(wrap) {
    var body = wrap.querySelector('[data-dha-desc-body]');
    var toggle = wrap.querySelector('[data-dha-desc-toggle]');
    if (!body || !toggle) return;
    var expanded = false;

    function measure() {
      if (expanded) return;
      wrap.classList.add('is-clamped');
      var overflows = body.scrollHeight > body.clientHeight + 1;
      if (!overflows) wrap.classList.remove('is-clamped');
      toggle.hidden = !overflows;
    }

    toggle.addEventListener('click', function () {
      expanded = !expanded;
      wrap.classList.toggle('is-clamped', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? 'Show less' : 'Read more';
    });
    measure();
    window.addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  function start() {
    document.querySelectorAll('[data-dha-estimator]').forEach(initEstimator);
    document.querySelectorAll('[data-dha-desc]').forEach(initDescription);
  }

  return { SIZES: SIZES, parseMap: parseMap, money: money, estimate: estimate, configFrom: configFrom, start: start };
});
